import { useContext, useMemo } from 'react'

import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import usePropertyAnytimeRate from 'hooks/Offers/usePropertyAnytimeRate'
import {
  canApplyPropertyAnytimeRate,
  canSourcePriceFromPropertyAnytimeRate,
  isPropertyAnytimeRateOccupancy,
} from 'lib/offer/propertyAnytimeRates'
import type { AccommodationSearchPresentationInput } from 'lib/search/accommodationSearchPresentation'
import { shouldHideAccommodationSearchPrice } from 'lib/search/accommodationSearchPresentation'
import { isStandardOccupancy } from 'lib/search/searchUtils'

export interface AnytimePricePresentationInput {
  offerType?: App.OfferType
  productType?: App.ProductType
  metadataHidePricing?: boolean
  filters?: App.OfferListFilters
  accommodationPropertyId?: string
  /** Length of stay the tile labels its price with, in nights. */
  displayedDuration?: number
  /**
   * Look the property up even when no anytime price is on show, so the caller can tell whether a
   * dynamic Flash campaign promotion is running. Search tiles badge the campaign on every search,
   * dated or not; the price it sources stays limited to anytime searches as before.
   */
  lookUpDynamicFlashPromotion?: boolean
}

export interface AnytimePricePresentation {
  hidePrice: boolean
  /**
   * The promotion rate whose dynamic Flash campaign is what keeps the price visible, and so the
   * rate the visible price must come from. Absent whenever anytime pricing isn't what's on show.
   */
  anytimePromotionRate?: App.Property.AnytimePromotionRate
  /** Whether the property is running a dynamic Flash campaign promotion. */
  hasDynamicFlashPromotion: boolean
}

/**
 * Resolves whether a search tile shows a price, the anytime rate behind it when a dynamic Flash
 * campaign promotion is why it's visible, and whether such a promotion is running at all.
 *
 * All three answers come from the one property lookup so the price shown, the decision to show
 * it and the badge can't disagree.
 */
export function useAnytimePricePresentation({
  offerType,
  productType,
  metadataHidePricing,
  filters,
  accommodationPropertyId,
  displayedDuration,
  lookUpDynamicFlashPromotion = false,
}: AnytimePricePresentationInput): AnytimePricePresentation {
  const globalSearchState = useContext(GlobalSearchStateContext)
  const hideAnytimePrices = !!globalSearchState?.hideAnytimePrices
  const hasStandardOccupancy = isStandardOccupancy(filters)
  // A search that never named an occupancy is searching the default one — two adults, which is
  // what an anytime rate is quoted for. Past that, the rate's own occupancy rule applies, which is
  // the offer page's rule rather than the shared policy's standard-occupancy exemption.
  const rooms = filters?.rooms
  const isAnytimeRateOccupancy = !rooms?.length || isPropertyAnytimeRateOccupancy(rooms)

  const presentationInput = useMemo<AccommodationSearchPresentationInput>(() => ({
    offerType,
    productType,
    metadataHidePricing,
    hideAnytimePrices,
    hasStandardOccupancy,
  }), [offerType, productType, metadataHidePricing, hideAnytimePrices, hasStandardOccupancy])

  // LPC is the only offer type running dynamic Flash campaigns, so nothing else has an anytime
  // rate to look up. Every other tile — and every non-search caller of the pricing hooks — stops
  // here, at the offer type it already has.
  const canSourceAnytimePrice = canSourcePriceFromPropertyAnytimeRate(offerType)
  // A dynamic Flash promotion outranks everything else search knows about the price: it decides
  // both that the price shows and what it is, and the tile has to lead with the price the offer
  // page will honour. So look the property up on any anytime search rather than only when it would
  // flip an otherwise hidden price — a limited time special already shows a price, and the
  // promotion still owns it. Metadata-hidden pricing is the exception: that price stays hidden.
  const isAnytimePriceOnShow = canSourceAnytimePrice &&
    hideAnytimePrices &&
    !metadataHidePricing
  // The campaign also badges the tile, on dated searches and marketing traffic too, so a caller
  // that asks for it looks the property up regardless. The rate itself only prices the tile where
  // an anytime price is on show.
  const isLookupRequired = isAnytimePriceOnShow || (canSourceAnytimePrice && lookUpDynamicFlashPromotion)
  const [anytimeRates] = usePropertyAnytimeRate(
    isLookupRequired ? accommodationPropertyId : undefined,
    { disabled: !isLookupRequired },
  )

  return useMemo<AnytimePricePresentation>(() => {
    const anytimePromotionRate = anytimeRates?.anytimePromotionRate
    // The promotion merchandises like a Flash offer, so it keeps the price visible whatever the
    // customer asked for — but it can only *be* that price for the stay it was quoted against,
    // which is the same guard the offer page applies before it shows the rate. Where it can't, the
    // tile keeps the pricing search gave it, as it did before campaigns ran.
    const hasDynamicFlashPromotion = anytimePromotionRate?.promotion.type === 'dynamicFlash'
    const canPriceFromPromotion = isAnytimePriceOnShow && hasDynamicFlashPromotion && canApplyPropertyAnytimeRate({
      rateDuration: anytimePromotionRate.duration,
      displayedDuration,
      hasStandardOccupancy: isAnytimeRateOccupancy,
    })

    return {
      hidePrice: shouldHideAccommodationSearchPrice({ ...presentationInput, hasDynamicFlashPromotion }),
      anytimePromotionRate: canPriceFromPromotion ? anytimePromotionRate : undefined,
      hasDynamicFlashPromotion,
    }
  }, [anytimeRates?.anytimePromotionRate, displayedDuration, isAnytimeRateOccupancy, isAnytimePriceOnShow, presentationInput])
}

export default useAnytimePricePresentation
